// CORE
import { executeRequest, type ApiResult } from '@/core/either';
import { http } from '@/core/api';

// TYPES
import type { CountryData } from '@/types/CountryType';
import type { State } from '@/types/RouteTransferType';
import type { GetCountriesParams } from '@/shared/common/types/country.type';

export const countryService = {
  /**
   * Obtiene la lista de países disponibles.
   * Con `withCompanyDocuments` el backend devuelve solo los países que admiten documentos de empresa.
   * @param params - Parámetros de consulta para el catálogo.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<CountryData[]>).
   */
  getCountries(
    { all = false, excludeBlocked = false, withCompanyDocuments = false }: GetCountriesParams = {}
  ): Promise<ApiResult<CountryData[]>> {
    return executeRequest(() =>
      http.get<CountryData[]>('route/b2b/countries', {
        params: { all, excludeBlocked, withCompanyDocuments },
      })
    );
  },

  /**
   * Obtiene la lista de países para uso externo.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<CountryData[]>).
   */
  getCountriesExternal(): Promise<ApiResult<CountryData[]>> {
    return executeRequest(() =>
      http.get<CountryData[]>('route/ext/countries', { params: { all: false } })
    );
  },

  /**
   * Obtiene la lista de estados/provincias por ID de país.
   * @param countryId - ID del país.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<State[]>).
   */
  getStateByCountry(countryId: number): Promise<ApiResult<State[]>> {
    return executeRequest(() =>
      http.get<State[]>(`route/b2b/countries/states/${countryId}`)
    );
  },

  /**
   * Obtiene la lista de estados/provincias por ID de país para uso externo.
   * @param countryId - ID del país.
   * @returns Promesa con el resultado tipado usando Either (ApiResult<State[]>).
   */
  getStateByCountryExternal(countryId: number): Promise<ApiResult<State[]>> {
    return executeRequest(() =>
      http.get<State[]>(`route/ext/states/${countryId}`)
    );
  },

  /**
   * Obtiene la lista de estados/provincias por código alpha2 del país.
   * @param alpha2Code - Código alpha2 del país (ej: 'US', 'CA', 'MX').
   * @returns Promesa con el resultado tipado usando Either (ApiResult<State[]>).
   */
  getStateByCountryAlpha2Code(alpha2Code: string): Promise<ApiResult<State[]>> {
    return executeRequest(() =>
      http.get<State[]>(`route/b2b/countries/states/by-code/${alpha2Code}`)
    );
  },
};
